import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import DetailsButton from 'components/lib/DetailsButton';
import { useAppSelector } from 'store/hook';
import { RecommendationType } from 'types/recommendations';

import RecommendationCategoryTag from './RecommendationCategoryTag';
import RecommendationMarkdown from './RecommendationMarkdown';
import useRecommendationsContext from './RecommendationsContext';
import { getRecommendationDefaultCategoryScheme, parseTextFromMarkDown } from './utils';

const Container = styled.div`
  min-width: 370px;
  min-height: 156px;
  max-height: 175px;
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border: solid 1px var(--line-gray);
  background-color: #fff;
  display: flex;
  flex-flow: row;
  flex-grow: 1;
  flex-basis: 48%;
  max-width: 48%;
  &:nth-child(odd):last-child {
  }

  @media (max-width: 1108px) {
    flex-basis: 100%;
    max-width: 100%;
  }
`;

interface IndicatorProps {
  gradientFrom: string;
  gradientTo: string;
}

const Indicator = styled.div<IndicatorProps>`
  width: 5px;
  min-width: 5px;
  border-radius: 4px;
  background-image: ${({ gradientFrom, gradientTo }) => `linear-gradient(to bottom, ${gradientFrom}, ${gradientTo})`};
`;

const Details = styled.div`
  padding: 20px;
  width: 100%;
`;

const Reasoning = styled.div`
  width: 100%;
  height: 57px;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
`;

const Includes = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 520px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
`;

const CategoryTagContainer = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const TitleContainer = styled.div`
  p {
    font-size: 16px;
  }
`;

type RecommendationCardProps = {
  recommendation: RecommendationType;
  detailsPath?: string;
};

const RecommendationCard = ({ recommendation, detailsPath = '/recommendations' }: RecommendationCardProps) => {
  const navigate = useNavigate();
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { search } = useLocation();
  const { filters, communityOptions, careTypeOptions } = useRecommendationsContext();
  const colorScheme = getRecommendationDefaultCategoryScheme(recommendation);
  const filterSelections = useMemo(() => {
    const filteredCommunities = communityOptions
      .filter(
        (co) =>
          filters.communities.includes(Number(co.value)) &&
          recommendation.report.community_ids.includes(Number(co.value)) &&
          !(selectedCommunity && filters.communities.includes(selectedCommunity.id))
      )
      .map((co) => co.label);
    const filteredCareTypes = careTypeOptions
      .filter(
        (co) =>
          filters.careTypes.includes(Number(co.value)) && recommendation.report.care_types.includes(Number(co.value))
      )
      .map((co) => co.label);

    return filteredCommunities.concat(filteredCareTypes);
  }, [recommendation, filters, communityOptions, careTypeOptions, selectedCommunity]);

  const handleDetailsClick = () => {
    navigate(`${detailsPath}/${recommendation.id}` + search);
  };

  const categories = [
    ...recommendation.config.data_points.reduce((acc, dp) => {
      if (dp.category) {
        acc.add(dp.category);
      }
      return acc;
    }, new Set<string>()),
  ];

  const descriptionPlain = parseTextFromMarkDown(recommendation.description);

  return (
    <Container>
      <Indicator gradientFrom={colorScheme.gradientFrom} gradientTo={colorScheme.gradientTo} />
      <Details>
        {filterSelections.length > 0 && <Includes>Includes selection {filterSelections.join(', ')}</Includes>}
        <TitleContainer>
          <RecommendationMarkdown>{recommendation.title}</RecommendationMarkdown>
        </TitleContainer>
        <Reasoning>{descriptionPlain}</Reasoning>
        <Footer>
          <CategoryTagContainer>
            {categories.map((c, index) => (
              <RecommendationCategoryTag key={index} category={c} />
            ))}
          </CategoryTagContainer>
          <DetailsButton onClick={handleDetailsClick} />
        </Footer>
      </Details>
    </Container>
  );
};

export default RecommendationCard;
